"use client";

import { ExternalLink, Search, Settings2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/** Static preview of the "Find material" pricing dialog — buy links, store discount, AI-estimate note (not live data). */
export function MarketingMaterialFinderMockup({ className }: { className?: string }) {
  const sources = [
    { retailer: "The Home Depot", price: "$1,898", title: "36 in. French-door refrigerator" },
    { retailer: "Lowe's", price: "$1,949", title: "Stainless French-door fridge" },
    { retailer: "Ferguson", price: "$1,872", title: "Pro account listing" },
  ];

  return (
    <div
      className={cn(
        "pointer-events-none select-none bg-background p-4 text-foreground sm:p-5",
        className
      )}
      aria-hidden
    >
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Search className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0">
          <p className="text-base font-semibold leading-snug">Material pricing</p>
          <p className="truncate text-xs text-muted-foreground">
            Stainless French-door refrigerator · priced for Austin, TX
          </p>
        </div>
      </div>

      <Card className="mt-4">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Your cost (per unit)</CardTitle>
            <span className="text-lg font-bold tabular-nums">$1,820</span>
          </div>
          <CardDescription className="pt-1">Customer price: $2,366 (30% markup applied)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs font-medium text-emerald-700">Includes 4% Ferguson supplier discount</p>

          <div>
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Where to buy</p>
            <ul className="space-y-2">
              {sources.map((s) => (
                <li key={s.retailer} className="rounded-md border px-3 py-2">
                  <span className="inline-flex items-start gap-1.5 text-sm font-medium text-primary">
                    <span className="min-w-0 break-words">{s.title}</span>
                    <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  </span>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {s.retailer} · {s.price}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
            <Settings2 className="h-3.5 w-3.5" aria-hidden />
            Set preferred suppliers &amp; discounts in Settings
          </span>

          <p className="text-[11px] leading-snug text-muted-foreground">
            AI can make mistakes — double-check prices and links before sending, even when a source is shown.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
